import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useTraining } from '../../context/TrainingContext';
import useTrainingSession from '../../hooks/training/useTrainingSession';
import SessionHeader from '../../components/training/SessionHeader';
import TrainingChatPanel from '../../components/training/TrainingChatPanel';
import AssessmentRequestDialog from '../../components/training/AssessmentRequestDialog';
import TrainingReportIssueDialog from '../../components/training/TrainingReportIssueDialog';
import SessionVoiceCallModal from '../../components/training/SessionVoiceCallModal';
import GoalSuggestDialog from '../../components/training/GoalSuggestDialog';
import trainingApi from '../../services/trainingApi';
import { getPlatformSettings } from '../../services/api';

// How long to wait for the server to confirm a goal suggested during a call.
const VOICE_GOAL_ACK_MS = 15000;

const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const TrainingSessionPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { socket, joinSession, leaveSession, user } = useTraining();

  const {
    messages,
    session,
    scenarioBrief,
    status,
    error,
    isPatientTyping,
    isSending,
    sendError,
    timeRemainingSeconds,
    timerWarning,
    totalTurns,
    assessmentsAdministered,
    isPaused,
    pauseInFlight,
    sendMessage,
    requestAssessment,
    proposeGoal,
    completeSession,
    pauseSession: pauseTherapistSession,
    resumeSession: resumeTherapistSession,
    clearSendError,
  } = useTrainingSession(id, socket);

  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [concludeDialogOpen, setConcludeDialogOpen] = useState(false);
  const [isRequestingAssessment, setIsRequestingAssessment] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [isProposingGoal, setIsProposingGoal] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [voiceModeActive, setVoiceModeActive] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [voiceModeEligible, setVoiceModeEligible] = useState(false);
  const voiceLivePanelRef = useRef(null);

  const token = localStorage.getItem('token');
  const voiceConfig = scenarioBrief?.voiceConfig || null;

  // Fetch platform settings for TTS voice defaults
  useEffect(() => {
    if (!token) return;
    getPlatformSettings(token)
      .then(data => setPlatformSettings(data))
      .catch(err => console.warn('[TrainingSession] Failed to load platform settings:', err.message));
  }, [token]);

  // Voice mode is eligible if scenario has it enabled and platform has it enabled
  useEffect(() => {
    const scenarioAvmEnabled = voiceConfig?.avmEnabled === true;
    const platformAvmEnabled = platformSettings?.trainingVoiceConfig?.enabled === true;
    setVoiceModeEligible(scenarioAvmEnabled && platformAvmEnabled);
  }, [voiceConfig, platformSettings]);

  // Handle 'new' session creation
  useEffect(() => {
    if (id === 'new') {
      const scenarioId = searchParams.get('scenarioId');
      const assignmentId = searchParams.get('assignmentId');
      if (scenarioId) {
        trainingApi.startSession(scenarioId, assignmentId)
          .then(response => {
            const body = response.data || response;
            const data = body.data || body;
            const newSession = data.session || data;
            navigate(`/training/session/${newSession._id}`, { replace: true });
          })
          .catch(err => {
            setSessionError(err.message || 'Failed to start session');
          });
      }
    }
  }, [id, searchParams, navigate]);

  // Join/leave socket room
  useEffect(() => {
    if (id && id !== 'new' && socket) {
      joinSession(id);
      return () => leaveSession(id);
    }
  }, [id, socket, joinSession, leaveSession]);

  // Therapist-practice sessions land back in the Simulation Lab on cancel/error
  // and use the canonical /training/session/:id URL throughout, but with
  // mode-aware chrome rendered downstream (see SessionHeader).
  const isTherapistPractice = session?.mode === 'therapist_practice';
  const isLecturerTest = session?.mode === 'lecturer_test';
  const cancelTarget = isLecturerTest
    ? '/training/scenarios'
    : (isTherapistPractice ? '/therapist/simulation-lab' : '/training');

  // Ownership gate: only the trainee whose session this is may send messages.
  // A lecturer/supervisor can open a student's live session URL to observe, but
  // must see a read-only view rather than a send box (the API 403s their writes
  // regardless — this just makes the UI honest). studentId holds the trainee's
  // userId for all modes (incl. therapist_practice, where it is the therapist).
  // If ownership can't be determined yet, default to owner so we never block the
  // real trainee — the backend remains the authoritative write guard.
  const sessionOwnerId = session?.studentId?._id || session?.studentId || null;
  const isOwner = (!sessionOwnerId || !user?._id)
    ? true
    : String(user._id) === String(sessionOwnerId);

  // Redirect on completion (not on time expired — timer is advisory). Lecturer
  // test-runs aren't graded, so they return to the library instead of the
  // student formulation flow.
  useEffect(() => {
    if (status === 'completed') {
      const timer = setTimeout(() => {
        navigate(isLecturerTest ? '/training/scenarios' : `/training/session/${id}/formulation`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [status, id, navigate, isLecturerTest]);

  const handleConclude = () => {
    setConcludeDialogOpen(true);
  };

  const handleConfirmConclude = async () => {
    try {
      await completeSession('student_concluded');
      setConcludeDialogOpen(false);
    } catch (err) {
      setSessionError(err.message || 'Failed to complete session');
      setConcludeDialogOpen(false);
    }
  };

  const handleAbandon = async () => {
    if (!window.confirm('Are you sure you want to cancel this session? It will be marked as abandoned and you can start a new one.')) return;
    try {
      await trainingApi.abandonSession(id);
      navigate(cancelTarget);
    } catch (err) {
      setSessionError(err.message || 'Failed to abandon session');
    }
  };

  const handleRequestAssessment = async (testKey) => {
    try {
      setIsRequestingAssessment(true);
      await requestAssessment(testKey);
      setAssessmentDialogOpen(false);
    } catch (err) {
      setSessionError(err.message || 'Failed to request assessment');
    } finally {
      setIsRequestingAssessment(false);
    }
  };

  // Suggest a goal (Dave, 23 Sep). In a live call it goes over the call's own
  // socket: the call is held in memory on the instance that started it, and a
  // REST request could land elsewhere and find no call. The server records it,
  // speaks it into the call, and the patient answers out loud. In text it is a
  // normal request and the patient's reply comes back in the response.
  const handleSuggestGoal = async ({ description, goalType }) => {
    setIsProposingGoal(true);
    try {
      if (voiceModeActive) {
        if (!socket) throw new Error('The call connection is not available. Please try again.');
        const result = await new Promise((resolve, reject) => {
          const timer = setTimeout(
            () => reject(new Error('The call did not confirm the goal. Please try again.')),
            VOICE_GOAL_ACK_MS
          );
          socket.emit('training:voice:propose_goal', { sessionId: id, description, goalType }, (ack) => {
            clearTimeout(timer);
            if (ack?.success) resolve(ack);
            else reject(new Error(ack?.error || 'Could not suggest the goal.'));
          });
        });
        if (result.deliveredToCall === false) {
          setSessionError('The goal was recorded, but the live call did not receive it, so the patient will not answer it out loud. Ending and restarting the call should fix this.');
        }
      } else {
        await proposeGoal({ description, goalType });
      }
      setGoalDialogOpen(false);
    } finally {
      setIsProposingGoal(false);
    }
  };

  // Loading state
  if (status === 'loading' || id === 'new') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '70vh' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {id === 'new' ? 'Starting session...' : 'Loading session...'}
        </Typography>
      </Box>
    );
  }

  // Error state
  if (status === 'error' || sessionError) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || sessionError || 'An error occurred'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(cancelTarget)}>
          {isTherapistPractice ? 'Return to Simulation Lab' : 'Return to Dashboard'}
        </Button>
      </Box>
    );
  }

  const availableAssessments = scenarioBrief?.availableAssessments || [];
  const durationSeconds = session?.startedAt
    ? Math.floor((Date.now() - new Date(session.startedAt).getTime()) / 1000)
    : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, flex: 1, overflow: 'hidden' }}>
      {/* Session header — three-way mode segmented control replaces the
          old separate autoplay + AVM headphone buttons. Mode is derived
          from the existing voiceModeActive / autoplayEnabled flags so
          downstream wiring (TTS auto-play, modal render) stays the same. */}
      <SessionHeader
        scenarioBrief={scenarioBrief}
        timeRemainingSeconds={timeRemainingSeconds}
        timerWarning={timerWarning}
        totalTurns={totalTurns}
        onConclude={handleConclude}
        onAbandon={handleAbandon}
        // Requesting an assessment no longer ends the call. It used to, "so the
        // assessment dialog and the live audio don't compete for the user's
        // mic", but neither side touches audio: the dialog is a picker, and the
        // server (handleAssessmentRequest) simulates and scores the patient's
        // answers in the background and adds one system line to the transcript.
        // Nothing is spoken. Therapists had to leave voice mode just to pick a
        // questionnaire (Dave, 23 Sep).
        onRequestAssessment={() => setAssessmentDialogOpen(true)}
        onSuggestGoal={status === 'active' && !isPaused ? () => setGoalDialogOpen(true) : undefined}
        availableAssessments={availableAssessments}
        assessmentsUsed={assessmentsAdministered}
        sessionMode={voiceModeActive ? 'call' : (autoplayEnabled ? 'voice' : 'chat')}
        onSessionModeChange={(next) => {
          if (next === 'call') {
            setAutoplayEnabled(false);
            setVoiceModeActive(true);
          } else if (next === 'voice') {
            setVoiceModeActive(false);
            setAutoplayEnabled(true);
          } else {
            setVoiceModeActive(false);
            setAutoplayEnabled(false);
          }
        }}
        voiceModeEligible={voiceModeEligible}
        onReportIssue={() => setReportIssueOpen(true)}
        isTherapistPractice={isTherapistPractice}
        isPaused={isPaused}
        pauseInFlight={pauseInFlight}
        onTogglePause={isTherapistPractice
          ? async () => {
              try {
                if (isPaused) {
                  await resumeTherapistSession();
                } else {
                  // Drop call mode (if active) before pausing — the
                  // VoiceLivePanel unmount will clean up the WS.
                  if (voiceModeActive) setVoiceModeActive(false);
                  await pauseTherapistSession();
                }
              } catch (err) {
                setSessionError(err?.response?.data?.error || err.message || 'Pause/resume failed');
              }
            }
          : undefined}
      />

      {/* Paused banner (therapist practice only) */}
      {isPaused && status === 'active' && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Practice paused — the clock is stopped. Resume when you're ready to continue.
        </Alert>
      )}

      {/* Lecturer test-run banner — make clear this is an ungraded verification run */}
      {isLecturerTest && status === 'active' && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Lecturer test run — this session is not graded and won't appear in the student review queue.
        </Alert>
      )}

      {/* Time expired advisory banner */}
      {timerWarning === 'expired' && status === 'active' && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Time limit reached. You may continue the session or conclude when ready.
        </Alert>
      )}

      {/* Completion banner */}
      {status === 'completed' && (
        <Alert severity="success" sx={{ borderRadius: 0 }}>
          {isLecturerTest
            ? 'Test run complete. Returning to the scenario library...'
            : 'Session completed. Redirecting to formulation...'}
        </Alert>
      )}

      {/* Chat panel — the SessionVoiceCallModal owns the VoiceLivePanel
          when AVM is active, so the inline chat panel always suppresses
          its own voice rendering. This avoids double-mounting the
          WebSocket and gives every audience (student, lecturer review,
          therapist practice) the same video-call experience. */}
      <TrainingChatPanel
        messages={messages}
        isPatientTyping={isPatientTyping}
        isSending={isSending}
        sendError={sendError}
        onSendMessage={sendMessage}
        onClearError={clearSendError}
        isSessionActive={status === 'active' && !isPaused}
        isOwner={isOwner}
        voiceConfig={voiceConfig}
        autoplayEnabled={autoplayEnabled}
        token={token}
        platformSettings={platformSettings}
        sx={{ flex: 1 }}
        voiceModeActive={false}
        voiceLivePanelRef={voiceLivePanelRef}
        trainingSocket={socket}
        trainingSessionId={id}
        onVoiceClose={() => setVoiceModeActive(false)}
      />

      {/* Voice "video call" overlay. Honours visibilityLevel: in blind
          mode the persona name is hidden so the brief stays unrevealed.
          NOTE: we do *not* call voiceLivePanelRef.current.end() here.
          handleEndSession inside VoiceLivePanel itself calls onClose,
          which is bound to this onEndCall — calling end() again from
          here would re-enter handleEndSession and recurse. Setting
          voiceModeActive=false unmounts the panel; its useEffect
          cleanup tears the WebSocket down once. */}
      <SessionVoiceCallModal
        open={voiceModeActive}
        personaName={
          scenarioBrief?.visibilityLevel === 'blind'
            ? 'New Patient'
            : (scenarioBrief?.personaName || scenarioBrief?.name || 'Patient')
        }
        personaImageUrl={scenarioBrief?.personaImageUrl || null}
        voiceLivePanelRef={voiceLivePanelRef}
        trainingSocket={socket}
        trainingSessionId={id}
        onEndCall={() => setVoiceModeActive(false)}
        // The call covers the screen, so the header's Request Assessment sits
        // behind it. Offer the same action inside the call, under the same
        // rules as the header button. The picker renders after this overlay,
        // so it opens on top of the call rather than behind it.
        onRequestAssessment={() => setAssessmentDialogOpen(true)}
        assessmentsHidden={scenarioBrief?.visibilityLevel === 'blind' && scenarioBrief?.blindModeAssessments === 'hidden'}
        assessmentsUnavailable={Array.isArray(availableAssessments) && availableAssessments.length === 0}
        onSuggestGoal={() => setGoalDialogOpen(true)}
      />

      {/* Rendered after the call overlay so it opens on top of the call. */}
      <GoalSuggestDialog
        open={goalDialogOpen}
        onClose={() => setGoalDialogOpen(false)}
        onSubmit={handleSuggestGoal}
        isSubmitting={isProposingGoal}
        inCall={voiceModeActive}
      />

      {/* Assessment request dialog */}
      <AssessmentRequestDialog
        open={assessmentDialogOpen}
        onClose={() => setAssessmentDialogOpen(false)}
        availableAssessments={availableAssessments}
        assessmentsUsed={assessmentsAdministered}
        onRequest={handleRequestAssessment}
        isRequesting={isRequestingAssessment}
      />

      {/* Report issue dialog */}
      <TrainingReportIssueDialog
        open={reportIssueOpen}
        onClose={() => setReportIssueOpen(false)}
        token={token}
        sessionId={id}
        scenarioBrief={scenarioBrief}
        totalTurns={totalTurns}
        assessmentsAdministered={assessmentsAdministered}
        voiceModeActive={voiceModeActive}
      />

      {/* Conclude session dialog */}
      <Dialog open={concludeDialogOpen} onClose={() => setConcludeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conclude Training Session</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you ready to end this session? Once concluded, you will proceed to the clinical formulation.
          </Typography>
          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
            <Typography variant="body2">Session Summary:</Typography>
            <Typography variant="body2">Turns: {totalTurns}</Typography>
            <Typography variant="body2">Duration: {formatDuration(durationSeconds)}</Typography>
            <Typography variant="body2">
              Assessments: {assessmentsAdministered.length > 0 ? assessmentsAdministered.join(', ') : 'None'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConcludeDialogOpen(false)}>Continue Session</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmConclude}>
            Conclude &amp; Write Formulation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingSessionPage;
